import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Share2, Check } from "lucide-react";
import { api } from "../lib/api";
import { ConfirmGround } from "./ConfirmGround";

const SITE = process.env.REACT_APP_BACKEND_URL;
const slug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const GRADE_STYLE = {
  ready: "border-[#4b8f6f] text-[#7dd3a8]",
  reported: "border-ochre text-ochre",
  none: "border-maasai text-maasai",
};

/** Which counties could host a Governors' League fixture, and which need a ground first. */
export const LeagueReadiness = () => {
  const [d, setD] = useState(null);
  const [grade, setGrade] = useState("all");
  const [confirming, setConfirming] = useState(null);

  useEffect(() => {
    api.get("/governors-league/readiness").then((r) => setD(r.data)).catch(() => setD(null));
  }, []);

  if (!d?.rows?.length) return null;
  const rows = grade === "all" ? d.rows : d.rows.filter((r) => r.grade === grade);
  const pct = Math.round((d.population_ready / (d.population_total || 1)) * 100);

  return (
    <section className="mt-16" data-testid="league-readiness">
      <h2 className="display-3">{d.heading}</h2>
      <p className="mt-5 max-w-3xl text-sm leading-relaxed text-zinc-400">{d.intro}</p>

      <div className="mt-8 grid gap-px bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4" data-testid="readiness-counts">
        {[["Could host now", d.counts.ready, `${pct}% of Kenyans live there`],
          ["Reported, needs checking", d.counts.reported, "A ground is named but unverified"],
          ["Nothing on file", d.counts.none, "No venue recorded at all"],
          ["A season in the ready counties", `$${(d.startable_season_usd || 0).toLocaleString()}`,
            `At $${(d.season_usd_per_county || 0).toLocaleString()} a county season`]].map(([k, v, note], i) => (
          <div key={k} className="bg-[#0b0b0b] p-6" data-testid={`readiness-count-${i}`}>
            <p className="mono text-[0.58rem] uppercase tracking-[0.16em] text-zinc-400">{k}</p>
            <p className="mt-3 font-display text-xl font-bold leading-none text-white md:text-2xl">{v}</p>
            <p className="mt-3 text-[0.56rem] uppercase tracking-[0.14em] text-ochre">{note}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 grid gap-px bg-zinc-800 md:grid-cols-3" data-testid="readiness-grades">
        {d.grades.map((g) => (
          <div key={g.id} className="bg-[#0b0b0b] p-5">
            <p className={`mono inline-block border px-2.5 py-1 text-[0.55rem] uppercase tracking-[0.16em] ${GRADE_STYLE[g.id]}`}>
              {g.label}
            </p>
            <p className="mt-3 text-xs leading-relaxed text-zinc-400">{g.meaning}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="readiness-filter">
        {[{ id: "all", label: "All 47 counties", n: d.counties },
          ...d.grades.map((g) => ({ id: g.id, label: g.label, n: d.counts[g.id] }))].map((g) => (
          <button
            key={g.id}
            type="button"
            onClick={() => setGrade(g.id)}
            data-testid={`readiness-filter-${g.id}`}
            className={`border px-4 py-2 text-[0.6rem] font-bold uppercase tracking-[0.14em] transition-colors ${
              grade === g.id ? "border-ochre bg-ochre/10 text-ochre" : "border-white/12 text-zinc-500 hover:text-white"
            }`}
          >
            {g.label} <span className="ml-2 text-zinc-500">{g.n}</span>
          </button>
        ))}
      </div>

      <div className="mt-6 overflow-x-auto border border-zinc-900">
        <table className="w-full min-w-[880px] text-left" data-testid="readiness-table">
          <thead>
            <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
              {["County", "Grade", "The ground", "Type and size", "What it still needs", "Check it or send it on"].map((h) => (
                <th key={h} className="mono px-4 py-3 text-[0.58rem] uppercase tracking-[0.16em] text-zinc-300">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr data-testid="readiness-empty">
                <td colSpan={6} className="px-4 py-6 text-sm text-zinc-400">
                  No county sits in that grade — every one of the 47 has at least one ground on file.
                </td>
              </tr>
            ) : null}
            {rows.map((r) => (
              <tr key={r.county} className="border-b border-zinc-900 align-top"
                data-testid={`readiness-row-${slug(r.county)}`}>
                <td className="px-4 py-4">
                  <Link to={`/counties/${slug(r.county)}`}
                    className="font-display text-sm uppercase text-white hover:text-ochre">
                    {r.county}
                  </Link>
                  <span className="mono mt-1 block text-[0.55rem] uppercase tracking-[0.14em] text-zinc-500">
                    {((r.population || 0) / 1e6).toFixed(2)}m people
                  </span>
                </td>
                <td className="px-4 py-4">
                  <span className={`mono inline-block border px-2 py-0.5 text-[0.5rem] uppercase tracking-[0.14em] ${GRADE_STYLE[r.grade]}`}>
                    {r.grade_label}
                  </span>
                </td>
                <td className="px-4 py-4 text-xs leading-relaxed text-zinc-300">
                  {r.main_venue || "—"}
                  {r.other_venues?.length ? (
                    <span className="mt-1 block text-[0.55rem] uppercase tracking-[0.14em] text-zinc-500">
                      + {r.other_venues.length} more on file
                    </span>
                  ) : null}
                </td>
                <td className="mono px-4 py-4 text-xs text-zinc-400">
                  {r.main_venue_type || "—"}
                  {r.main_venue_area_m2 ? (
                    <span className="mt-1 block text-zinc-500">{r.main_venue_area_m2.toLocaleString()} m²</span>
                  ) : null}
                </td>
                <td className="px-4 py-4 text-xs leading-relaxed text-zinc-400">{r.needs}</td>
                <td className="flex flex-col items-start gap-2 px-4 py-4">
                  {r.grade !== "ready" ? (
                    <button type="button" onClick={() => setConfirming(r)}
                      data-testid={`readiness-confirm-${slug(r.county)}`}
                      className="mono inline-flex items-center gap-1.5 whitespace-nowrap border border-ochre/50 px-3 py-1.5 text-[0.55rem] uppercase tracking-[0.14em] text-ochre transition-colors hover:bg-ochre/10">
                      <Check size={11} /> Confirm
                    </button>
                  ) : null}
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(
                      `${r.county} and the Governors' League — ${r.grade_label}. ` +
                      `${r.main_venue || "No ground on file"}. ${r.needs} ` +
                      `${SITE}/api/card/readiness/${slug(r.county)}.png`
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`readiness-share-${slug(r.county)}`}
                    className="mono inline-flex items-center gap-1.5 whitespace-nowrap border border-white/12 px-3 py-1.5 text-[0.55rem] uppercase tracking-[0.14em] text-zinc-400 transition-colors hover:border-ochre hover:text-ochre"
                  >
                    <Share2 size={11} /> Share
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-5 max-w-3xl text-xs leading-relaxed text-zinc-500">{d.note}</p>
    <Link to={d.correct_at} className="mono mt-4 inline-block text-[0.6rem] uppercase tracking-[0.16em] text-ochre underline underline-offset-4"
        data-testid="readiness-correct">
        Know a ground we have missed? Tell us and we will add it
      </Link>
      {confirming ? (
        <ConfirmGround county={confirming.county} venue={confirming.main_venue}
          onClose={() => setConfirming(null)} />
      ) : null}
    </section>
  );
};
